import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CREATION_TYPES } from '../config/creationTypes';

interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  photo_url?: string;
  activated: boolean;
  balance: number;
}

interface HomeScreenProps {
  userInfo: UserInfo;
}

export function HomeScreen({ userInfo }: HomeScreenProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFeatureClick = (featureId: string) => {
    // Navigate directly to the creation flow with the selected feature
    navigate('/create', { state: { selectedFeature: featureId } });
  };

  // Main creation types with descriptions
  const mainCreationOptions = [
    { 
      id: 'c-roll', 
      icon: 'bolt', 
      label: t('create.types.c-roll.header'), 
      description: t('create.types.c-roll.description'),
      proOnly: false,
      comingSoon: false
    },
    { 
      id: 'podcast-moments', 
      icon: 'auto_awesome', 
      label: t('create.types.podcast-moments.header'), 
      description: t('create.types.podcast-moments.description'),
      proOnly: false,
      comingSoon: false
    },
    { 
      id: 'podcast-series', 
      icon: 'podcasts', 
      label: t('create.types.podcast-series.header'), 
      description: t('create.types.podcast-series.description'),
      proOnly: true,
      comingSoon: false 
    },
    { 
      id: 'ai-captions', 
      icon: 'closed_caption', 
      label: t('create.types.ai-captions.header'), 
      description: t('create.types.ai-captions.description'),
      proOnly: false,
      comingSoon: true
    },
  ];

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Pro Banner */}
      <div className="fixed top-0 left-0 right-0 z-[100] shadow-[0_4px_12px_-6px_rgba(0,0,0,0.1)]" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/subscription')}
            >
              <span className="bg-black text-white dark:bg-white dark:text-black px-2 py-0.5 rounded-md text-sm font-semibold">{t('homeScreen.proBannerLabel')}</span>
              <span className="text-theme-text/90">{t('homeScreen.proBannerDescription')}</span>
            </div>
            <button 
              onClick={() => navigate('/balance')}
              className="flex items-center gap-1.5 text-theme-hint hover:text-theme-text transition-colors p-2 -mr-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
            >
              <span className="text-base">✨</span>
              <span className="font-medium">{userInfo.balance}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Page Title */}
      <div className="pt-[72px] px-4 pb-6">
        <h2 className="text-2xl font-bold text-theme-text/90">{t('create.title')}</h2>
        <p className="text-theme-hint mt-1">{t('homeScreen.subtitle')}</p>
      </div>

      {/* Creation Types Grid */}
      <div className="px-4 pb-24">
        <div className="grid grid-cols-1 gap-4">
          {mainCreationOptions.map(option => (
            <div 
              key={option.id} 
              className="relative p-4 bg-black/[0.03] dark:bg-white/[0.03] rounded-xl hover:bg-black/[0.05] dark:hover:bg-white/[0.05] transition-all cursor-pointer"
              onClick={() => {
                const creationType = CREATION_TYPES.find(type => type.id === option.id);
                if (creationType && !option.comingSoon) handleFeatureClick(option.id);
              }}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-14 h-14 bg-black/5 dark:bg-white/5 rounded-xl flex items-center justify-center mb-2">
                  <span className="material-symbols-outlined text-2xl">{option.icon}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium">{option.label}</h3>
                    {option.proOnly && (
                      <span className="bg-[#2D62FF] text-white text-xs font-bold px-1.5 py-0.5 rounded-md">
                        {t('homeScreen.proFeatureTag')}
                      </span>
                    )}
                    {option.comingSoon && (
                      <span className="bg-[#8B5CF6]/80 text-white text-xs font-bold px-1.5 py-0.5 rounded-md">
                        {t('homeScreen.soonFeatureTag')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-theme-hint mt-1">{option.description}</p>
                </div>
                <div className="flex-shrink-0 self-center">
                  <span className="material-symbols-outlined text-theme-hint">arrow_forward_ios</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 